import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth, useClerk } from '@clerk/clerk-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { track, resetUser } from '@/lib/analytics';
import { FiArrowLeft, FiX, FiFolder, FiMoon, FiPlus } from 'react-icons/fi';
import { useTempCheck } from '@/hooks/useTempCheck';
import { CalmFilterModal } from '@/components/CalmFilterModal';
import { CalendarSyncCard } from '@/components/settings/CalendarSyncCard';
import { ProfessionalInvitesCard } from '@/components/settings/ProfessionalInvitesCard';
import {
  Button,
  Card,
  Input,
  PhoneNumberInput,
  normalizeUsPhoneNumber,
  Paywall,
} from '@/components';
import { apiJson, ApiError } from '@/lib/api';
import { useHouseholds } from '@/lib/household-context';
import {
  formatPhoneNumber,
  formatMemberSince,
  formatInviteDate,
} from '@/lib/ui';
import {
  getInviteStatus,
  getFromUserName,
  getMemberName,
  type UserResponse,
  type HouseholdResponse,
  type InviteListResponse,
  type InviteResponse,
  type BeneficiaryResponse,
} from '@/lib/types';

function SettingsPage() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { enabled: tempCheckEnabled, setEnabled: setTempCheckEnabled } = useTempCheck();

  // Track screen view
  useEffect(() => {
    track('Settings Screen');
  }, []);

  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [acceptModalVisible, setAcceptModalVisible] = useState(false);
  const [selectedInvite, setSelectedInvite] = useState<InviteResponse | null>(
    null
  );
  const [coparentName, setCoparentName] = useState('');
  const [coparentPhone, setCoparentPhone] = useState('');
  const [inviteSent, setInviteSent] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [beneficiaryModalVisible, setBeneficiaryModalVisible] = useState(false);
  const [beneficiaryName, setBeneficiaryName] = useState('');
  const [beneficiaryType, setBeneficiaryType] = useState<
    'CHILD' | 'PET' | 'OTHER'
  >('CHILD');
  const [beneficiaryBirthdate, setBeneficiaryBirthdate] = useState('');
  const [editingBeneficiary, setEditingBeneficiary] =
    useState<BeneficiaryResponse | null>(null);
  const [editBirthdateModalVisible, setEditBirthdateModalVisible] =
    useState(false);
  const [editBirthdateValue, setEditBirthdateValue] = useState('');
  const [paywallModalVisible, setPaywallModalVisible] = useState(false);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportEmail, setExportEmail] = useState('');
  const [exportMessages, setExportMessages] = useState(true);
  const [exportAccounting, setExportAccounting] = useState(true);
  const [exportCalendar, setExportCalendar] = useState(true);
  const [exportCheckIns, setExportCheckIns] = useState(true);
  const [exportExchangeCompliance, setExportExchangeCompliance] = useState(true);
  const [exportJournal, setExportJournal] = useState(false);
  const [exportExpenseDisputeSummary, setExportExpenseDisputeSummary] =
    useState(false);
  const [exportHearingPrepBrief, setExportHearingPrepBrief] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportSuccess, setExportSuccess] = useState(false);
  const [error, setError] = useState('');
  const [leaveModalVisible, setLeaveModalVisible] = useState(false);
  const [leaveConfirmText, setLeaveConfirmText] = useState('');
  const [calmModalVisible, setCalmModalVisible] = useState(false);
  const [calmEnabled, setCalmEnabled] = useState(false);
  const [calmQuietStart, setCalmQuietStart] = useState(21 * 60);
  const [calmQuietEnd, setCalmQuietEnd] = useState(7 * 60);

  // Fetch user data
  const userQuery = useQuery({
    queryKey: ['user'],
    queryFn: () => apiJson<UserResponse>(getToken, '/v1/user'),
  });

  useEffect(() => {
    if (!userQuery.data) return;
    setCalmEnabled(Boolean(userQuery.data.calm_filter_enabled));
    if (typeof userQuery.data.calm_filter_quiet_start_minutes === 'number') {
      setCalmQuietStart(userQuery.data.calm_filter_quiet_start_minutes);
    }
    if (typeof userQuery.data.calm_filter_quiet_end_minutes === 'number') {
      setCalmQuietEnd(userQuery.data.calm_filter_quiet_end_minutes);
    }
  }, [
    userQuery.data?.calm_filter_enabled,
    userQuery.data?.calm_filter_quiet_start_minutes,
    userQuery.data?.calm_filter_quiet_end_minutes,
  ]);

  // Fetch households
  const householdsQuery = useQuery({
    queryKey: ['households'],
    queryFn: async () => {
      try {
        return await apiJson<HouseholdResponse[]>(getToken, '/v1/households');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return [] as HouseholdResponse[];
        }
        throw err;
      }
    },
  });

  // Fetch invites
  const invitesQuery = useQuery({
    queryKey: ['invites'],
    queryFn: async () => {
      try {
        return await apiJson<InviteListResponse>(getToken, '/v1/invites');
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 403 || err.status === 404)
        ) {
          return { received: [], sent: [] } as InviteListResponse;
        }
        throw err;
      }
    },
  });

  // Create invite mutation
  const inviteMutation = useMutation({
    mutationFn: async () => {
      const phoneNumber = normalizeUsPhoneNumber(coparentPhone);
      if (!phoneNumber) throw new Error('Please provide a valid phone number.');
      if (!coparentName.trim()) throw new Error('Please enter a name.');
      if (
        userQuery.data?.phone_number &&
        phoneNumber === userQuery.data.phone_number
      ) {
        throw new Error("You can't send an invite to your own phone number.");
      }
      const payload = {
        to_name: coparentName.trim(),
        to_phone_number: phoneNumber,
      };
      return apiJson(getToken, '/v1/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      setInviteSent(true);
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't send invite.";
      setError(errorMessage);
    },
  });

  // Accept invite mutation
  const acceptInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      return apiJson(getToken, `/v1/invites/${inviteId}/accept`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setAcceptModalVisible(false);
      setSelectedInvite(null);
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't accept invite.";
      setError(errorMessage);
    },
  });

  // Reject invite mutation
  const rejectInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      return apiJson(getToken, `/v1/invites/${inviteId}/reject`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't reject invite.";
      setError(errorMessage);
    },
  });

  // Delete invite mutation
  const deleteInviteMutation = useMutation({
    mutationFn: async (inviteId: number) => {
      return apiJson(getToken, `/v1/invites/${inviteId}`, { method: 'DELETE' });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't cancel invite.";
      setError(errorMessage);
    },
  });

  // Delete beneficiary mutation
  const deleteBeneficiaryMutation = useMutation({
    mutationFn: async (data: {
      householdId: number;
      beneficiaryId: number;
    }) => {
      return apiJson(
        getToken,
        `/v1/households/${data.householdId}/beneficiaries/${data.beneficiaryId}`,
        { method: 'DELETE' }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['households'] });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't remove beneficiary.";
      setError(errorMessage);
    },
  });

  // Add beneficiary mutation
  const addBeneficiaryMutation = useMutation({
    mutationFn: async (data: {
      householdId: number;
      name: string;
      type: 'CHILD' | 'PET' | 'OTHER';
      birthdate?: string;
    }) => {
      return apiJson(
        getToken,
        `/v1/households/${data.householdId}/beneficiaries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            beneficiaries: [
              {
                name: data.name,
                type: data.type,
                ...(data.birthdate && { birthdate: data.birthdate }),
              },
            ],
          }),
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setBeneficiaryModalVisible(false);
      setBeneficiaryName('');
      setBeneficiaryType('CHILD');
      setBeneficiaryBirthdate('');
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't add beneficiary.";
      setError(errorMessage);
    },
  });

  // Update beneficiary mutation
  const updateBeneficiaryMutation = useMutation({
    mutationFn: async (data: {
      householdId: number;
      beneficiaryId: number;
      birthdate: string;
    }) => {
      return apiJson(
        getToken,
        `/v1/households/${data.householdId}/beneficiaries/${data.beneficiaryId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ birthdate: data.birthdate }),
        }
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setEditBirthdateModalVisible(false);
      setEditingBeneficiary(null);
      setEditBirthdateValue('');
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't update beneficiary.";
      setError(errorMessage);
    },
  });

  const leaveHouseholdMutation = useMutation({
    mutationFn: async () => {
      if (!firstHousehold) throw new Error('No household found');
      return apiJson(getToken, `/v1/households/${firstHousehold.id}/leave`, {
        method: 'POST',
      });
    },
    onSuccess: async () => {
      track('Leave Household');
      setLeaveModalVisible(false);
      setLeaveConfirmText('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['households'] }),
        queryClient.invalidateQueries({ queryKey: ['invites'] }),
        queryClient.invalidateQueries({ queryKey: ['activation'] }),
        queryClient.invalidateQueries({ queryKey: ['expenses'] }),
        queryClient.invalidateQueries({ queryKey: ['payments'] }),
        queryClient.invalidateQueries({ queryKey: ['calendar'] }),
        queryClient.invalidateQueries({ queryKey: ['conversations'] }),
      ]);
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't leave household.";
      setError(errorMessage);
    },
  });

  const calmFilterMutation = useMutation({
    mutationFn: async (payload: {
      calm_filter_enabled: boolean;
      calm_filter_quiet_start_minutes: number;
      calm_filter_quiet_end_minutes: number;
    }) =>
      apiJson<UserResponse>(getToken, '/v1/user', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }),
    onSuccess: async (updated) => {
      track('Calm Filter Updated', {
        enabled: updated.calm_filter_enabled,
      });
      await queryClient.invalidateQueries({ queryKey: ['user'] });
      setCalmModalVisible(false);
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't update calm filter.";
      alert(errorMessage);
    },
  });

  // Delete account mutation
  const deleteAccountMutation = useMutation({
    mutationFn: async () => {
      return apiJson(getToken, '/v1/user', { method: 'DELETE' });
    },
    onSuccess: async () => {
      setDeleteModalVisible(false);
      setDeleteConfirmText('');
      resetUser();
      await signOut();
      navigate({ to: '/auth' });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't delete account.";
      setError(errorMessage);
    },
  });

  // Export data mutation
  const exportMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        email: exportEmail,
        include_messages: exportMessages,
        include_accounting: exportAccounting,
        include_calendar: exportCalendar,
        include_check_ins: exportCheckIns,
        include_exchange_compliance: exportExchangeCompliance,
        include_journal: exportJournal,
        include_expense_dispute_summary: exportExpenseDisputeSummary,
        include_hearing_prep_brief: exportHearingPrepBrief,
      };
      if (exportStartDate.trim()) payload.start_date = exportStartDate.trim();
      if (exportEndDate.trim()) payload.end_date = exportEndDate.trim();
      return apiJson(getToken, '/v1/export/email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    },
    onSuccess: () => {
      setExportSuccess(true);
      track('Export Email Sent', {
        include_messages: exportMessages,
        include_accounting: exportAccounting,
        include_calendar: exportCalendar,
        include_check_ins: exportCheckIns,
        include_exchange_compliance: exportExchangeCompliance,
        include_journal: exportJournal,
        include_expense_dispute_summary: exportExpenseDisputeSummary,
        include_hearing_prep_brief: exportHearingPrepBrief,
        start_date: exportStartDate || undefined,
        end_date: exportEndDate || undefined,
      });
    },
    onError: (err: unknown) => {
      const errorMessage =
        err instanceof Error ? err.message : "Couldn't send export.";
      setError(errorMessage);
    },
  });

  const user = userQuery.data;
  const { currentHouseholdId, setCurrentHouseholdId } = useHouseholds();
  const households = householdsQuery.data ?? [];
  const firstHousehold =
    households.find((h) => h.id === currentHouseholdId) ?? households[0];
  const [creatingHousehold, setCreatingHousehold] = useState(false);

  // "Add another household": create a new household, switch to it, and route
  // into the new-household setup. Always available (the switcher chip only
  // appears once the user has 2+ households).
  const handleAddHousehold = async () => {
    track('Add Household Tapped');
    setCreatingHousehold(true);
    try {
      const created = await apiJson<{ id: number }>(getToken, '/v1/households', {
        method: 'POST',
      });
      await queryClient.invalidateQueries({ queryKey: ['households'] });
      setCurrentHouseholdId(created.id, { track: false });
      track('Add Household Completed', { active_household_id: created.id });
      navigate({ to: '/app/onboarding/children' });
    } catch {
      // best-effort
    } finally {
      setCreatingHousehold(false);
    }
  };
  const householdSize = firstHousehold?.members?.length ?? 0;
  const isSoloHousehold = householdSize === 1;
  const sentInvites = invitesQuery.data?.sent ?? [];
  const receivedInvites = invitesQuery.data?.received ?? [];
  const pendingReceivedInvites = receivedInvites.filter(
    (invite) => !invite.accepted_user_id && !invite.rejected_user_id
  );
  const hasPendingInvite = sentInvites.some(
    (invite) => getInviteStatus(invite) === 'pending'
  );

  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to sign out?')) {
      resetUser();
      await signOut();
      navigate({ to: '/auth' });
    }
  };

  const handleAcceptInvite = (invite: InviteResponse) => {
    setSelectedInvite(invite);
    setAcceptModalVisible(true);
  };

  const handleRejectInvite = (invite: InviteResponse) => {
    if (window.confirm(`Reject the invite from ${getFromUserName(invite)}?`)) {
      rejectInviteMutation.mutate(invite.id);
    }
  };

  const handleCancelInvite = (invite: InviteResponse) => {
    const status = getInviteStatus(invite);
    if (status !== 'pending') {
      alert(`This request has already been ${status}.`);
      return;
    }
    if (window.confirm(`Cancel the request to ${invite.to_name}?`)) {
      deleteInviteMutation.mutate(invite.id);
    }
  };

  const handleDeleteBeneficiary = (beneficiary: BeneficiaryResponse) => {
    if (!firstHousehold) return;
    if (
      window.confirm(
        `Remove ${beneficiary.name}? This action cannot be undone.`
      )
    ) {
      deleteBeneficiaryMutation.mutate({
        householdId: firstHousehold.id,
        beneficiaryId: beneficiary.beneficiary_id,
      });
    }
  };

  const handleAddBeneficiary = () => {
    if (!firstHousehold) {
      setError('No household found.');
      return;
    }
    if (!beneficiaryName.trim()) {
      setError('Please enter a name.');
      return;
    }
    setError('');
    addBeneficiaryMutation.mutate({
      householdId: firstHousehold.id,
      name: beneficiaryName.trim(),
      type: beneficiaryType,
      birthdate: beneficiaryBirthdate || undefined,
    });
  };

  const handleEditBirthdate = (beneficiary: BeneficiaryResponse) => {
    setEditingBeneficiary(beneficiary);
    setEditBirthdateValue(beneficiary.birthdate || '');
    setEditBirthdateModalVisible(true);
  };

  const handleSaveBirthdate = () => {
    if (!firstHousehold || !editingBeneficiary) return;
    if (!editBirthdateValue) {
      setError('Please select a birthdate.');
      return;
    }
    setError('');
    updateBeneficiaryMutation.mutate({
      householdId: firstHousehold.id,
      beneficiaryId: editingBeneficiary.beneficiary_id,
      birthdate: editBirthdateValue,
    });
  };

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleOpenExportModal = () => {
    track('Export Data Button Clicked');
    if (user?.email) {
      setExportEmail(user.email);
    }
    setExportSuccess(false);
    setError('');
    setExportMessages(true);
    setExportAccounting(true);
    setExportCalendar(true);
    setExportCheckIns(true);
    setExportExchangeCompliance(true);
    setExportJournal(false);
    setExportExpenseDisputeSummary(false);
    setExportHearingPrepBrief(false);
    setExportStartDate('');
    setExportEndDate('');
    setExportModalVisible(true);
  };

  const handleSendExport = () => {
    if (!isValidEmail(exportEmail)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (
      !exportMessages &&
      !exportAccounting &&
      !exportCalendar &&
      !exportCheckIns &&
      !exportExchangeCompliance &&
      !exportJournal &&
      !exportExpenseDisputeSummary &&
      !exportHearingPrepBrief
    ) {
      setError('Please select at least one type of data to export.');
      return;
    }
    setError('');
    exportMutation.mutate();
  };

  const isExportFormValid =
    isValidEmail(exportEmail) &&
    (exportMessages ||
      exportAccounting ||
      exportCalendar ||
      exportCheckIns ||
      exportExchangeCompliance ||
      exportJournal ||
      exportExpenseDisputeSummary ||
      exportHearingPrepBrief);

  const isDeleteConfirmValid =
    deleteConfirmText.toLowerCase() === 'delete permanently';

  // Loading state
  if (userQuery.isLoading) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center'>
        <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
      </div>
    );
  }

  // Error state
  if (userQuery.isError) {
    return (
      <div className='min-h-screen bg-sky-light flex items-center justify-center p-6'>
        <Card className='max-w-md w-full text-center'>
          <p className='text-gray-500 mb-4'>Failed to load user information</p>
          <Button onClick={() => userQuery.refetch()}>Retry</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-sky-light'>
      {/* Mobile Header - hidden on desktop */}
      <header className='lg:hidden bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-lg mx-auto px-6 py-4'>
          <Link
            to='/app/expenses'
            className='inline-flex items-center gap-2 text-primary hover:underline'
          >
            <FiArrowLeft /> Back to Expenses
          </Link>
        </div>
      </header>

      {/* Desktop Header - shown only on large screens */}
      <header className='hidden lg:block bg-sky-light border-b border-gray-200 sticky top-0 z-10'>
        <div className='max-w-2xl mx-auto px-8 py-4'>
          <h1
            className='text-xl font-semibold text-gray-900'
            style={{
              fontFamily:
                "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
            }}
          >
            Settings
          </h1>
        </div>
      </header>

      <div className='max-w-lg lg:max-w-2xl mx-auto px-6 lg:px-8 py-8 space-y-4'>
        {/* Mobile title - hidden on desktop (shown in header instead) */}
        <h1
          className='lg:hidden text-2xl font-bold text-gray-900 text-center mb-6'
          style={{
            fontFamily:
              "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          }}
        >
          Settings
        </h1>

        {/* Received Invites */}
        {pendingReceivedInvites.length > 0 && (
          <Card>
            <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Co-parent Invite
            </h2>
            {pendingReceivedInvites.map((invite) => (
              <div
                key={invite.id}
                className='py-3 border-b border-gray-100 last:border-0'
              >
                <p className='font-bold text-gray-900'>
                  {getFromUserName(invite)} wants to add you
                </p>
                <p className='text-sm text-gray-500 mt-1'>
                  Join their household to start sharing expenses and payments
                </p>
                <div className='flex gap-3 mt-3'>
                  <Button
                    variant='danger'
                    size='sm'
                    onClick={() => handleRejectInvite(invite)}
                    disabled={rejectInviteMutation.isPending}
                  >
                    Decline
                  </Button>
                  <Button
                    size='sm'
                    onClick={() => handleAcceptInvite(invite)}
                    disabled={acceptInviteMutation.isPending}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </Card>
        )}

        {/* Profile Section */}
        {user && (
          <Card>
            <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Profile
            </h2>
            <div className='flex items-center gap-4'>
              <div className='w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg'>
                {(user.first_name?.[0] ?? '').toUpperCase()}
                {(user.last_name?.[0] ?? '').toUpperCase()}
              </div>
              <div>
                <p className='font-bold text-gray-900'>
                  {user.first_name && user.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user.first_name || 'No name set'}
                </p>
                <p className='text-sm text-gray-500'>
                  Member since {formatMemberSince(user.created_at)}
                </p>
              </div>
            </div>
          </Card>
        )}

        {/* Household Section */}
        {firstHousehold && firstHousehold.members.length > 0 && (
          <Card>
            <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Household
            </h2>
            {firstHousehold.members.map((member, index) => {
              const isCurrentUser = member.user_id === user?.id;
              return (
                <div
                  key={member.user_id}
                  className={`py-3 ${
                    index < firstHousehold.members.length - 1
                      ? 'border-b border-gray-100'
                      : ''
                  }`}
                >
                  <p className='font-bold text-gray-900'>
                    {getMemberName(member)}
                    {isCurrentUser && (
                      <span className='text-gray-500 font-normal'> (You)</span>
                    )}
                  </p>
                  {member.email && (
                    <div className='flex justify-between mt-1'>
                      <span className='text-sm text-gray-500'>Email</span>
                      <span className='text-sm text-gray-900'>
                        {member.email}
                      </span>
                    </div>
                  )}
                  {member.phone_number && (
                    <div className='flex justify-between mt-1'>
                      <span className='text-sm text-gray-500'>Phone</span>
                      <span className='text-sm text-gray-900'>
                        {formatPhoneNumber(member.phone_number)}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Beneficiaries */}
            <div className='h-px bg-gray-100 my-3' />
            <p className='text-xs font-semibold text-gray-500 mb-2'>
              People & Pets
            </p>
            <div className='flex flex-wrap gap-2'>
              {firstHousehold.beneficiaries?.map((beneficiary) => (
                <div
                  key={beneficiary.beneficiary_id}
                  className='inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 rounded-full border border-primary/20'
                >
                  <span className='text-sm font-semibold text-gray-900'>
                    {beneficiary.name}
                    {beneficiary.type === 'CHILD' && ' 👶'}
                    {beneficiary.type === 'PET' && ' 🐾'}
                    {beneficiary.type === 'OTHER' && ' 👤'}
                  </span>
                  <Link
                    to='/app/vault/$beneficiaryId'
                    params={{
                      beneficiaryId: String(beneficiary.beneficiary_id),
                    }}
                    className='ml-1 p-0.5 hover:bg-primary/20 rounded-full transition-colors'
                    title={`${beneficiary.name}'s Vault`}
                  >
                    <FiFolder size={14} className='text-primary' />
                  </Link>
                  {!beneficiary.birthdate && beneficiary.type === 'CHILD' && (
                    <button
                      onClick={() => handleEditBirthdate(beneficiary)}
                      className='px-1.5 py-0.5 text-xs font-medium text-amber-700 bg-amber-100 rounded hover:bg-amber-200 transition-colors'
                      title='Add birthdate'
                    >
                      + DOB
                    </button>
                  )}
                  <button
                    onClick={() => handleDeleteBeneficiary(beneficiary)}
                    className='p-0.5 hover:bg-gray-200 rounded-full transition-colors'
                    disabled={deleteBeneficiaryMutation.isPending}
                  >
                    <FiX size={14} className='text-gray-500' />
                  </button>
                </div>
              ))}
              <button
                onClick={() => setBeneficiaryModalVisible(true)}
                disabled={addBeneficiaryMutation.isPending}
                className='inline-flex items-center gap-1 px-3 py-1.5 bg-primary/5 rounded-full border border-dashed border-primary/30 hover:bg-primary/10 transition-colors'
              >
                <span className='text-sm font-semibold text-primary'>
                  + Add
                </span>
              </button>
            </div>

            {/* Add another household (multi-household). Always available so a
                user co-parenting with a second family can create that
                household; the switcher only appears once they have 2+. */}
            <div className='h-px bg-gray-100 my-3' />
            <button
              onClick={handleAddHousehold}
              disabled={creatingHousehold}
              className='inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline disabled:opacity-60'
            >
              <FiPlus size={16} />
              {creatingHousehold ? 'Creating…' : 'Add another household'}
            </button>
          </Card>
        )}

        {/* Co-parent Invite Section */}
        {isSoloHousehold && (
          <>
            {!hasPendingInvite && (
              <Card>
                <div className='text-center'>
                  <h3 className='font-bold text-gray-900 mb-2'>
                    Add your co-parent
                  </h3>
                  <p className='text-sm text-gray-500 mb-4'>
                    Add your co-parent to start tracking shared expenses and
                    payments together.
                  </p>
                  <Button fullWidth onClick={() => setInviteModalVisible(true)}>
                    Add co-parent
                  </Button>
                </div>
              </Card>
            )}

            {/* Sent Invites */}
            {sentInvites.length > 0 && (
              <Card>
                <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
                  Co-parent
                </h2>
                {sentInvites.map((invite) => {
                  const status = getInviteStatus(invite);
                  return (
                    <div
                      key={invite.id}
                      className='py-3 border-b border-gray-100 last:border-0'
                    >
                      <div className='flex justify-between items-center'>
                        <span className='font-bold text-gray-900'>
                          {invite.to_name}
                        </span>
                        <span
                          className={`px-2 py-0.5 rounded-lg text-xs font-bold ${
                            status === 'accepted'
                              ? 'bg-green-100 text-green-700'
                              : status === 'rejected'
                              ? 'bg-red-100 text-red-600'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {status === 'accepted'
                            ? 'Accepted'
                            : status === 'rejected'
                            ? 'Rejected'
                            : 'Pending'}
                        </span>
                      </div>
                      <div className='flex justify-between mt-1'>
                        <span className='text-sm text-gray-500'>
                          {formatPhoneNumber(invite.to_phone_number)}
                        </span>
                        <span className='text-xs text-gray-500'>
                          {formatInviteDate(invite.created_at)}
                        </span>
                      </div>
                      {status === 'pending' && (
                        <Button
                          variant='danger'
                          size='sm'
                          className='mt-3'
                          onClick={() => handleCancelInvite(invite)}
                          disabled={deleteInviteMutation.isPending}
                        >
                          Cancel
                        </Button>
                      )}
                    </div>
                  );
                })}
              </Card>
            )}
          </>
        )}

        {/* Contact Information */}
        {user && (
          <Card>
            <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Contact Information
            </h2>
            <div className='flex justify-between py-2'>
              <span className='text-gray-500'>Phone</span>
              <span className='font-semibold text-gray-900'>
                {formatPhoneNumber(user.phone_number)}
              </span>
            </div>
            <div className='h-px bg-gray-100 my-1' />
            <div className='flex justify-between py-2'>
              <span className='text-gray-500'>Email</span>
              <span className='font-semibold text-gray-900'>
                {user.email ?? 'Not set'}
              </span>
            </div>
          </Card>
        )}

        {/* Chat Settings */}
        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
            Chat
          </h2>
          <div className='flex items-center justify-between py-2'>
            <div className='flex-1 mr-3'>
              <p className='font-bold text-gray-900 text-[15px]'>Temp Check</p>
              <p className='text-xs text-gray-400 mt-0.5 leading-4'>
                Checks your message temperature before sending
              </p>
            </div>
            <label className='relative inline-flex items-center cursor-pointer'>
              <input
                type='checkbox'
                checked={tempCheckEnabled}
                onChange={(e) => {
                  track('tone_check_toggled', { enabled: e.target.checked });
                  setTempCheckEnabled(e.target.checked);
                }}
                className='sr-only peer'
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/30 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
            </label>
          </div>
          <div className='h-px bg-gray-100 my-2' />
          <button
            type='button'
            onClick={() => setCalmModalVisible(true)}
            className='w-full flex items-center justify-between py-2 text-left hover:bg-gray-50 rounded-lg px-1 -mx-1'
          >
            <div>
              <p className='font-bold text-gray-900 text-[15px] flex items-center gap-2'>
                <FiMoon className='w-4 h-4 text-primary' />
                Calm filter
              </p>
              <p className='text-xs text-gray-400 mt-0.5'>
                {user?.calm_filter_enabled
                  ? 'Quiet hours enabled for chat notifications'
                  : 'Set quiet hours for chat notifications'}
              </p>
            </div>
            <span className='text-sm text-primary font-semibold'>Edit</span>
          </button>
        </Card>

        <Card>
          <CalendarSyncCard />
        </Card>

        <Card>
          <ProfessionalInvitesCard />
        </Card>

        {/* Subscription Status */}
        {user && (
          <Card>
            <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
              Subscription
            </h2>
            <div className='flex justify-between items-center py-2'>
              <span className='text-gray-500'>Status</span>
              <span
                className={`px-3 py-1 rounded-xl text-sm font-bold ${
                  user.subscribed
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                {user.subscribed ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className='h-px bg-gray-100 my-3' />
            <button
              onClick={() => setPaywallModalVisible(true)}
              className='w-full py-3 px-4 bg-primary/10 rounded-xl text-center text-primary font-bold hover:bg-primary/20 transition-colors'
            >
              View subscription options
            </button>
          </Card>
        )}

        {/* Export Data */}
        <Card>
          <h2 className='text-xs font-bold text-gray-500 uppercase tracking-wider mb-3'>
            Export Data
          </h2>
          <p className='text-sm text-gray-500 mb-3'>
            Export your messages, expenses, payments, calendar, check-ins,
            exchange compliance, and optional journal as
            court-ready documents.
          </p>
          <Button fullWidth onClick={handleOpenExportModal}>
            Export data via email
          </Button>
        </Card>

        {/* Actions */}
        <Card>
          <Button variant='danger' fullWidth onClick={handleSignOut}>
            Sign out
          </Button>
          {!isSoloHousehold && (
            <div className='mt-3'>
              <Button
                variant='ghost'
                fullWidth
                className='border border-red-200 text-red-600 hover:bg-red-50'
                onClick={() => {
                  setLeaveConfirmText('');
                  setLeaveModalVisible(true);
                }}
              >
                Leave household
              </Button>
            </div>
          )}
          <div className='mt-3'>
            <Button
              variant='ghost'
              fullWidth
              className='border border-red-200 text-red-600 hover:bg-red-50'
              onClick={() => setDeleteModalVisible(true)}
            >
              Delete account
            </Button>
          </div>
        </Card>
      </div>

      {/* Add Co-parent Modal */}
      {inviteModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            {inviteSent ? (
              <>
                <div className='text-center py-4'>
                  <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                    <svg
                      className='w-8 h-8 text-green-600'
                      fill='none'
                      viewBox='0 0 24 24'
                      stroke='currentColor'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth={2}
                        d='M5 13l4 4L19 7'
                      />
                    </svg>
                  </div>
                  <h3 className='text-lg font-bold text-gray-900 mb-2'>
                    Invite sent!
                  </h3>
                  <p className='text-gray-500 mb-6'>
                    We've texted {coparentName.trim() || 'your co-parent'} an
                    invitation to join Harbor. Once they accept, you'll both be
                    able to track shared expenses and payments together.
                  </p>
                  <div className='space-y-3'>
                    <Button
                      variant='secondary'
                      fullWidth
                      onClick={() => {
                        navigator.clipboard.writeText(
                          'https://harborcoparent.com?notification=invite'
                        );
                        alert('Invitation link copied to clipboard.');
                      }}
                    >
                      Copy invitation link
                    </Button>
                    <Button
                      fullWidth
                      onClick={() => {
                        setInviteModalVisible(false);
                        setCoparentName('');
                        setCoparentPhone('');
                        setInviteSent(false);
                        setError('');
                        queryClient.invalidateQueries({
                          queryKey: ['invites'],
                        });
                        queryClient.invalidateQueries({
                          queryKey: ['households'],
                        });
                      }}
                    >
                      Done
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className='flex justify-between items-center mb-4'>
                  <h2 className='text-2xl font-bold text-gray-900'>
                    Add your co-parent
                  </h2>
                  <button
                    onClick={() => {
                      setInviteModalVisible(false);
                      setCoparentName('');
                      setCoparentPhone('');
                      setError('');
                    }}
                    className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                  >
                    <FiX size={20} />
                  </button>
                </div>
                <p className='text-gray-500 mb-6'>
                  Enter your co-parent's information to send them an invite.
                </p>
                <div className='space-y-4'>
                  <Input
                    label='Co-parent name'
                    value={coparentName}
                    onChange={(e) => setCoparentName(e.target.value)}
                    placeholder='Taylor'
                    disabled={inviteMutation.isPending}
                  />
                  <PhoneNumberInput
                    label='Phone number'
                    value={coparentPhone}
                    onChangeText={setCoparentPhone}
                    disabled={inviteMutation.isPending}
                  />
                  {error && (
                    <p className='text-sm text-red-500 text-center'>{error}</p>
                  )}
                  <Button
                    fullWidth
                    onClick={() => inviteMutation.mutate()}
                    disabled={inviteMutation.isPending}
                    loading={inviteMutation.isPending}
                  >
                    {inviteMutation.isPending ? 'Sending…' : 'Send invite'}
                  </Button>
                  <Button
                    variant='secondary'
                    fullWidth
                    onClick={() => {
                      setInviteModalVisible(false);
                      setCoparentName('');
                      setCoparentPhone('');
                      setError('');
                    }}
                    disabled={inviteMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Accept Invite Modal */}
      {acceptModalVisible && selectedInvite && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6'>
          <Card className='max-w-md w-full'>
            <h2 className='text-xl font-bold text-gray-900 mb-3'>
              Accept invite?
            </h2>
            <p className='text-gray-500 mb-6'>
              Join {getFromUserName(selectedInvite)}'s household? You'll be able
              to share expenses and payments together.
            </p>
            <div className='flex gap-3'>
              <Button
                variant='secondary'
                fullWidth
                onClick={() => {
                  setAcceptModalVisible(false);
                  setSelectedInvite(null);
                }}
                disabled={acceptInviteMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                onClick={() => acceptInviteMutation.mutate(selectedInvite.id)}
                disabled={acceptInviteMutation.isPending}
                loading={acceptInviteMutation.isPending}
              >
                Accept
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Delete Account Modal */}
      {deleteModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-2xl font-bold text-gray-900'>
                Delete account
              </h2>
              <button
                onClick={() => {
                  setDeleteModalVisible(false);
                  setDeleteConfirmText('');
                  setError('');
                }}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                disabled={deleteAccountMutation.isPending}
              >
                <FiX size={20} />
              </button>
            </div>
            <p className='text-gray-500 mb-4'>
              This action is permanent and cannot be undone. Your account and
              all associated data will be deleted.
            </p>
            <p className='text-gray-500 mb-6'>
              If you have an active subscription, you must cancel it separately
              through the App Store or Google Play Store.
            </p>
            <div className='space-y-4'>
              <Input
                label='Type "delete permanently" to confirm'
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder='delete permanently'
                disabled={deleteAccountMutation.isPending}
              />
              {error && (
                <p className='text-sm text-red-500 text-center'>{error}</p>
              )}
              <Button
                variant='danger'
                fullWidth
                onClick={() => deleteAccountMutation.mutate()}
                disabled={
                  deleteAccountMutation.isPending || !isDeleteConfirmValid
                }
                loading={deleteAccountMutation.isPending}
                className='bg-red-500 hover:bg-red-600 text-white'
              >
                Delete my account
              </Button>
              <Button
                variant='secondary'
                fullWidth
                onClick={() => {
                  setDeleteModalVisible(false);
                  setDeleteConfirmText('');
                  setError('');
                }}
                disabled={deleteAccountMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Beneficiary Modal */}
      {beneficiaryModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6'>
          <Card className='max-w-md w-full'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-xl font-bold text-gray-900'>
                Add Beneficiary
              </h2>
              <button
                onClick={() => {
                  setBeneficiaryModalVisible(false);
                  setBeneficiaryName('');
                  setBeneficiaryType('CHILD');
                  setBeneficiaryBirthdate('');
                  setError('');
                }}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              >
                <FiX size={20} />
              </button>
            </div>

            <div className='space-y-4'>
              <Input
                label='Name'
                value={beneficiaryName}
                onChange={(e) => setBeneficiaryName(e.target.value)}
                placeholder='Enter name'
                disabled={addBeneficiaryMutation.isPending}
              />

              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>
                  Type
                </label>
                <div className='flex gap-2'>
                  {(['CHILD', 'PET', 'OTHER'] as const).map((type) => (
                    <button
                      key={type}
                      onClick={() => setBeneficiaryType(type)}
                      disabled={addBeneficiaryMutation.isPending}
                      className={`flex-1 py-2 px-4 rounded-xl text-sm font-semibold transition-colors ${
                        beneficiaryType === type
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {type === 'CHILD'
                        ? 'Child'
                        : type === 'PET'
                        ? 'Pet'
                        : 'Other'}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>
                  Birthdate (optional)
                </label>
                <input
                  type='date'
                  value={beneficiaryBirthdate}
                  onChange={(e) => setBeneficiaryBirthdate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={addBeneficiaryMutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
              </div>

              {error && (
                <p className='text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='flex gap-3 pt-2'>
                <Button
                  variant='secondary'
                  fullWidth
                  onClick={() => {
                    setBeneficiaryModalVisible(false);
                    setBeneficiaryName('');
                    setBeneficiaryType('CHILD');
                    setBeneficiaryBirthdate('');
                    setError('');
                  }}
                  disabled={addBeneficiaryMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  onClick={handleAddBeneficiary}
                  disabled={
                    addBeneficiaryMutation.isPending || !beneficiaryName.trim()
                  }
                  loading={addBeneficiaryMutation.isPending}
                >
                  Add
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Birthdate Modal */}
      {editBirthdateModalVisible && editingBeneficiary && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6'>
          <Card className='max-w-sm w-full'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-lg font-bold text-gray-900'>
                Add Birthdate for {editingBeneficiary.name}
              </h2>
              <button
                onClick={() => {
                  setEditBirthdateModalVisible(false);
                  setEditingBeneficiary(null);
                  setEditBirthdateValue('');
                  setError('');
                }}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
              >
                <FiX size={20} />
              </button>
            </div>

            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-semibold text-gray-600 mb-2'>
                  Birthdate
                </label>
                <input
                  type='date'
                  value={editBirthdateValue}
                  onChange={(e) => setEditBirthdateValue(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  disabled={updateBeneficiaryMutation.isPending}
                  className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                />
              </div>

              {error && (
                <p className='text-sm text-red-500 text-center'>{error}</p>
              )}

              <div className='flex gap-3 pt-2'>
                <Button
                  variant='secondary'
                  fullWidth
                  onClick={() => {
                    setEditBirthdateModalVisible(false);
                    setEditingBeneficiary(null);
                    setEditBirthdateValue('');
                    setError('');
                  }}
                  disabled={updateBeneficiaryMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  onClick={handleSaveBirthdate}
                  disabled={
                    updateBeneficiaryMutation.isPending || !editBirthdateValue
                  }
                  loading={updateBeneficiaryMutation.isPending}
                >
                  Save
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Export Modal */}
      {exportModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto'>
            <div className='flex justify-between items-center mb-4'>
              <h2 className='text-2xl font-bold text-gray-900'>Export Data</h2>
              <button
                onClick={() => {
                  setExportModalVisible(false);
                  setExportSuccess(false);
                  setError('');
                }}
                className='p-2 hover:bg-gray-100 rounded-full transition-colors'
                disabled={exportMutation.isPending}
              >
                <FiX size={20} />
              </button>
            </div>

            {exportSuccess ? (
              <div className='text-center py-4'>
                <div className='w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4'>
                  <svg
                    className='w-8 h-8 text-green-600'
                    fill='none'
                    viewBox='0 0 24 24'
                    stroke='currentColor'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M5 13l4 4L19 7'
                    />
                  </svg>
                </div>
                <h3 className='text-lg font-bold text-gray-900 mb-2'>
                  Export sent!
                </h3>
                <p className='text-gray-500 mb-6'>
                  Check your email for the files.
                </p>
                <Button
                  fullWidth
                  onClick={() => {
                    setExportModalVisible(false);
                    setExportSuccess(false);
                  }}
                >
                  Done
                </Button>
              </div>
            ) : (
              <>
                <p className='text-gray-500 mb-6'>
                  Choose what to export. We'll send it to your email as
                  court-ready documents.
                </p>
                <div className='space-y-4'>
                  <Input
                    label='Email address'
                    type='email'
                    value={exportEmail}
                    onChange={(e) => setExportEmail(e.target.value)}
                    placeholder='your@email.com'
                    disabled={exportMutation.isPending}
                  />

                  <div>
                    <label className='block text-sm font-semibold text-gray-600 mb-3'>
                      Include in export
                    </label>
                    <div className='space-y-3'>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportMessages}
                          onChange={(e) => setExportMessages(e.target.checked)}
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Messages
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportAccounting}
                          onChange={(e) =>
                            setExportAccounting(e.target.checked)
                          }
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Accounting (expenses & payments)
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportCalendar}
                          onChange={(e) => setExportCalendar(e.target.checked)}
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Calendar
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportCheckIns}
                          onChange={(e) => setExportCheckIns(e.target.checked)}
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          GPS check-ins
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportExchangeCompliance}
                          onChange={(e) =>
                            setExportExchangeCompliance(e.target.checked)
                          }
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Parenting Time Compliance Report
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportExpenseDisputeSummary}
                          onChange={(e) =>
                            setExportExpenseDisputeSummary(e.target.checked)
                          }
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Expense Dispute Summary
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportHearingPrepBrief}
                          onChange={(e) =>
                            setExportHearingPrepBrief(e.target.checked)
                          }
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Hearing Prep Brief
                        </span>
                      </label>
                      <label className='flex items-center gap-3 cursor-pointer'>
                        <input
                          type='checkbox'
                          checked={exportJournal}
                          onChange={(e) => setExportJournal(e.target.checked)}
                          disabled={exportMutation.isPending}
                          className='w-5 h-5 rounded border-gray-300 text-primary focus:ring-primary'
                        />
                        <span className='text-gray-900 font-medium'>
                          Journal (private, yours only)
                        </span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className='block text-sm font-semibold text-gray-600 mb-2'>
                      Date range (optional)
                    </label>
                    <p className='text-xs text-gray-500 mb-2'>
                      Leave blank to include all dates for selected sections.
                    </p>
                    <div className='flex flex-col sm:flex-row gap-3'>
                      <div className='flex-1'>
                        <span className='text-xs text-gray-500 block mb-1'>From</span>
                        <input
                          type='date'
                          value={exportStartDate}
                          onChange={(e) => setExportStartDate(e.target.value)}
                          disabled={exportMutation.isPending}
                          className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                        />
                      </div>
                      <div className='flex-1'>
                        <span className='text-xs text-gray-500 block mb-1'>To</span>
                        <input
                          type='date'
                          value={exportEndDate}
                          onChange={(e) => setExportEndDate(e.target.value)}
                          disabled={exportMutation.isPending}
                          className='w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary'
                        />
                      </div>
                    </div>
                  </div>

                  {error && (
                    <p className='text-sm text-red-500 text-center'>{error}</p>
                  )}

                  <Button
                    fullWidth
                    onClick={handleSendExport}
                    disabled={exportMutation.isPending || !isExportFormValid}
                    loading={exportMutation.isPending}
                  >
                    {exportMutation.isPending ? 'Sending…' : 'Send export'}
                  </Button>
                  <Button
                    variant='secondary'
                    fullWidth
                    onClick={() => {
                      setExportModalVisible(false);
                      setError('');
                    }}
                    disabled={exportMutation.isPending}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {leaveModalVisible && (
        <div className='fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-4'>
          <div className='bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6'>
            <h2 className='text-xl font-bold text-gray-900 mb-2'>Leave household</h2>
            <p className='text-sm text-gray-500 mb-4'>
              You&apos;ll start a new solo household. Shared records stay with the household you
              leave. Type <strong>LEAVE</strong> to confirm.
            </p>
            <Input
              label='Confirmation'
              value={leaveConfirmText}
              onChange={(e) => setLeaveConfirmText(e.target.value)}
              placeholder='LEAVE'
            />
            <div className='flex gap-3 mt-6'>
              <Button
                variant='secondary'
                fullWidth
                onClick={() => {
                  setLeaveModalVisible(false);
                  setLeaveConfirmText('');
                }}
              >
                Cancel
              </Button>
              <Button
                variant='danger'
                fullWidth
                onClick={() => leaveHouseholdMutation.mutate()}
                loading={leaveHouseholdMutation.isPending}
                disabled={
                  leaveConfirmText.trim().toUpperCase() !== 'LEAVE' ||
                  leaveHouseholdMutation.isPending
                }
              >
                Leave household
              </Button>
            </div>
          </div>
        </div>
      )}

      <CalmFilterModal
        open={calmModalVisible}
        onClose={() => setCalmModalVisible(false)}
        enabled={calmEnabled}
        onEnabledChange={setCalmEnabled}
        quietStartMinutes={calmQuietStart}
        quietEndMinutes={calmQuietEnd}
        isApplying={calmFilterMutation.isPending}
        onApply={(values) => {
          setCalmEnabled(values.enabled);
          setCalmQuietStart(values.quietStartMinutes);
          setCalmQuietEnd(values.quietEndMinutes);
          calmFilterMutation.mutate({
            calm_filter_enabled: values.enabled,
            calm_filter_quiet_start_minutes: values.quietStartMinutes,
            calm_filter_quiet_end_minutes: values.quietEndMinutes,
          });
        }}
      />

      {/* Paywall Modal */}
      {paywallModalVisible && (
        <div className='fixed inset-0 bg-white z-50 overflow-y-auto'>
          <div className='sticky top-0 z-10 flex justify-end p-4 bg-white/80 backdrop-blur-sm'>
            <button
              onClick={() => setPaywallModalVisible(false)}
              className='p-2 hover:bg-gray-100 rounded-full transition-colors'
            >
              <FiX size={24} />
            </button>
          </div>
          <Paywall
            getToken={getToken}
            onPurchaseSuccess={async () => {
              await queryClient.invalidateQueries({ queryKey: ['user'] });
              setPaywallModalVisible(false);
            }}
            userEmail={user?.email}
          />
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/app/settings')({
  component: SettingsPage,
});
